import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type {
  TeamProfileForInterview,
  InterviewData,
  InterviewResponse,
} from "@/types/team-profiles";
import { TOTAL_QUESTIONS } from "@/types/team-profiles";

// ---------------------------------------------------------------------------
// Fetch all active team profiles (for the profile selector)
// ---------------------------------------------------------------------------

export function useTeamProfilesForInterview() {
  return useQuery({
    queryKey: ["team-profiles-interview"],
    queryFn: async (): Promise<TeamProfileForInterview[]> => {
      const { data, error } = await (supabase as any)
        .from("team_profiles")
        .select(
          "id, full_name, job_title, department, territory, company, interview_data, interview_questions_answered, interview_completed_at"
        )
        .eq("is_active", true)
        .order("sort_order");

      if (error) throw error;
      return (data || []) as TeamProfileForInterview[];
    },
    staleTime: 2 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Fetch a single team profile by ID
// ---------------------------------------------------------------------------

export function useTeamProfile(profileId: string | null) {
  return useQuery({
    queryKey: ["team-profile-interview", profileId],
    queryFn: async (): Promise<TeamProfileForInterview | null> => {
      if (!profileId) return null;

      const { data, error } = await (supabase as any)
        .from("team_profiles")
        .select(
          "id, full_name, job_title, department, territory, company, interview_data, interview_questions_answered, interview_completed_at"
        )
        .eq("id", profileId)
        .single();

      if (error) throw error;
      return data as TeamProfileForInterview;
    },
    enabled: !!profileId,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Save an interview response (real DB write)
// ---------------------------------------------------------------------------

export function useSaveInterviewResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      profileId,
      questionId,
      questionText,
      responseText,
      responseType = "text",
    }: {
      profileId: string;
      questionId: number;
      questionText: string;
      responseText: string;
      responseType?: "text" | "audio" | "both";
    }) => {
      // 1. Fetch current interview_data
      const { data: profile, error: fetchError } = await (supabase as any)
        .from("team_profiles")
        .select("interview_data, interview_questions_answered")
        .eq("id", profileId)
        .single();

      if (fetchError) throw fetchError;

      const existingData: InterviewData = profile.interview_data || {};
      const existingResponses: InterviewResponse[] =
        existingData.responses || [];

      // 2. Upsert the response (replace if same question_id exists)
      const newResponse: InterviewResponse = {
        question_id: questionId,
        question_text: questionText,
        response_text: responseText,
        response_type: responseType,
        answered_at: new Date().toISOString(),
      };

      const filtered = existingResponses.filter(
        (r) => r.question_id !== questionId
      );
      const updatedResponses = [...filtered, newResponse];

      // 3. Calculate count + completion
      const answeredCount = updatedResponses.length;
      const isComplete = answeredCount >= TOTAL_QUESTIONS;

      const updatedData: InterviewData = {
        ...existingData,
        responses: updatedResponses,
      };

      // 4. Write back to DB
      const { error: updateError } = await (supabase as any)
        .from("team_profiles")
        .update({
          interview_data: updatedData,
          interview_questions_answered: answeredCount,
          interview_completed_at: isComplete
            ? new Date().toISOString()
            : null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", profileId);

      if (updateError) throw updateError;

      return { answeredCount, isComplete };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["team-profile-interview", variables.profileId],
      });
      queryClient.invalidateQueries({
        queryKey: ["team-profiles-interview"],
      });
      toast.success("Response saved");
      if (data.isComplete) {
        toast.success("All questions answered! Interview complete.");
      }
    },
    onError: (error) => {
      console.error("Error saving interview response:", error);
      toast.error("Failed to save response");
    },
  });
}
